import React, { FunctionComponent } from "react";
import {
  ArrowNarrowLeftIcon,
  ArrowNarrowRightIcon,
} from "@heroicons/react/solid";
import { navigate } from "gatsby";
import { range } from "lodash";
import { useTranslation } from "react-i18next";

const DOTS = Symbol();

const usePagination = ({
  totalPageCount,
  siblingCount = 1,
  currentPage,
}: {
  totalPageCount: number;
  siblingCount: number;
  currentPage: number;
}) => {
  // Pages count is determined as siblingCount + firstPage + lastPage + currentPage + 2*DOTS
  const totalPageNumbers = siblingCount * 2 + 5;

  /*
      Case 1:
      If the number of pages is less than the page numbers we want to show in our
      paginationComponent, we return the range [1..totalPageCount]
    */
  if (totalPageNumbers >= totalPageCount) {
    return range(1, totalPageCount + 1);
  }

  /*
    	Calculate left and right sibling index and make sure they are within range 1 and totalPageCount
    */
  const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
  const rightSiblingIndex = Math.min(
    currentPage + siblingCount,
    totalPageCount
  );

  /*
      We do not show dots just when there is just one page number to be inserted between the extremes of sibling and the page limits i.e 1 and totalPageCount. Hence we are using leftSiblingIndex > 2 and rightSiblingIndex < totalPageCount - 2
    */
  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < totalPageCount - 1;

  const firstPageIndex = 1;
  const lastPageIndex = totalPageCount;

  /*
    	Case 2: No left dots to show, but rights dots to be shown
    */
  if (!shouldShowLeftDots && shouldShowRightDots) {
    let leftItemCount = 3 + 2 * siblingCount;
    let leftRange = range(1, leftItemCount + 1);

    return [...leftRange, DOTS, totalPageCount];
  }

  /*
    	Case 3: No right dots to show, but left dots to be shown
    */
  if (shouldShowLeftDots && !shouldShowRightDots) {
    let rightItemCount = 3 + 2 * siblingCount;
    let rightRange = range(
      totalPageCount - rightItemCount + 1,
      totalPageCount + 1
    );
    return [firstPageIndex, DOTS, ...rightRange];
  }

  /*
    	Case 4: Both left and right dots to be shown
    */
  if (shouldShowLeftDots && shouldShowRightDots) {
    let middleRange = range(leftSiblingIndex, rightSiblingIndex + 1);
    return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex];
  }
};

type PaginationProps = {
  totalPageCount: number;
  currentPage: number;
  pageLinkGetter?: (page: number) => string;
  onPageChange?: (page: number) => void;
};

const Pagination: FunctionComponent<PaginationProps> = ({
  totalPageCount,
  currentPage,
  pageLinkGetter,
  onPageChange,
}) => {
  const pagination = usePagination({
    totalPageCount,
    siblingCount: 3,
    currentPage,
  });
  const getPrev = () => Math.max(1, currentPage - 1);
  const getNext = () => Math.min(totalPageCount, currentPage + 1);
  const { t } = useTranslation();

  if (totalPageCount === 1) return null;

  const onClick =
    (page: number, cond = true) =>
    () => {
      if (!cond) return;
      if (pageLinkGetter) navigate(pageLinkGetter(page));
      if (onPageChange) onPageChange(page);
    };

  return (
    <nav className="px-4 flex items-center justify-between sm:px-0">
      <div className="-mt-px w-0 flex-1 flex">
        {currentPage > 1 && (
          <div
            onClick={onClick(getPrev())}
            className="px-3 py-2 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
          >
            <ArrowNarrowLeftIcon
              className="mr-3 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
            {t("common:pagination.previous")}
          </div>
        )}
      </div>
      <div className="hidden md:-mt-px md:flex space-x-2">
        {pagination!.map((page, i) => {
          if (page === DOTS)
            return (
              <span
                key={`pag${i}`}
                className="text-gray-500 dark:text-gray-400 px-3 py-2 inline-flex items-center text-sm font-medium"
              >
                ...
              </span>
            );

          return (
            <div
              key={`pag${i}`}
              onClick={onClick(page as number, page !== currentPage)}
              className={[
                "px-3 py-2 inline-flex items-center text-sm font-medium border-b-2 cursor-pointer",
                page === currentPage
                  ? "text-primary border-primary"
                  : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 border-transparent",
              ].join(" ")}
            >
              {page}
            </div>
          );
        })}
      </div>
      <div className="-mt-px w-0 flex-1 flex justify-end">
        {currentPage < totalPageCount && (
          <div
            onClick={onClick(getNext())}
            className="px-3 py-2 inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 cursor-pointer"
          >
            {t("common:pagination.next")}
            <ArrowNarrowRightIcon
              className="ml-3 h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </div>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
